import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { getRecordArray, getString } from "@/lib/pageContent";

interface TestimonialsSectionProps {
  content?: Record<string, unknown>;
}

const fallbackTestimonials = [
  {
    text: "Mahirah's leadership training was one of the most impactful learning experiences I've had. She guided us through each session with patience and insight, helping me understand how to lead with empathy and clarity.",
    name: "Priya",
    role: "Working Professional & Coach, Netherlands",
  },
  {
    text: "Mahirah is a true gem! Her ability to truly listen and understand, even the unspoken, helped me uncover hidden strengths and break free from self-doubt.",
    name: "Fahad",
    role: "Businessperson, UK",
  },
  {
    text: "Mahirah's ability to blend clarity, empathy, and structure makes her sessions impactful across levels — from students to senior leaders. Her corporate grounding adds to her credibility.",
    name: "Engineering College Professor",
    role: "Davangere, Karnataka",
  },
  {
    text: "Working with Mahirah was a transformative experience. She helped me clarify my career goals and motivated me to create a tailored action plan.",
    name: "Dipak L",
    role: "Working Professional, France",
  },
];

const TestimonialsSection = ({ content }: TestimonialsSectionProps) => {
  const testimonials = getRecordArray<Record<string, unknown>>(content?.items, fallbackTestimonials).map((item, index) => ({
    text: getString(item.text, fallbackTestimonials[index]?.text ?? ""),
    name: getString(item.name, fallbackTestimonials[index]?.name ?? ""),
    role: getString(item.role, fallbackTestimonials[index]?.role ?? ""),
  }));
  const title = getString(content?.title, "What Our Clients Experience");
  const description = getString(
    content?.description,
    "At MVIBE, transformation isn't a promise — it's a pattern. Here's what professionals across industries and geographies say about working with Mahirah."
  );
  const [current, setCurrent] = useState(0);
  const next = useCallback(() => setCurrent((p) => (p + 1) % testimonials.length), [testimonials.length]);
  const prev = useCallback(() => setCurrent((p) => (p - 1 + testimonials.length) % testimonials.length), [testimonials.length]);

  useEffect(() => {
    const id = setInterval(next, 6000);
    return () => clearInterval(id);
  }, [next]);

  return (
    <section className="relative overflow-hidden bg-secondary py-10">
      <Quote className="absolute right-6 top-6 h-28 w-28 rotate-180 text-accent/15" />
      <div className="container relative z-10 mx-auto px-4">
        <div className="mx-auto mb-6 max-w-2xl text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-heading font-bold text-secondary-foreground md:text-5xl"
          >
            {title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-3 font-sans text-base text-secondary-foreground/70"
          >
            {description}
          </motion.p>
        </div>

        <div className="mx-auto max-w-3xl">
          <div className="relative flex min-h-[200px] items-center">
            <button
              type="button"
              onClick={prev}
              className="absolute left-0 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-card/10 transition-colors hover:bg-card/20 md:-left-12"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="h-5 w-5 text-secondary-foreground" aria-hidden="true" />
            </button>
            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.4 }}
                className="px-12 text-center"
              >
                <p className="text-lg italic leading-relaxed text-secondary-foreground md:text-xl">"{testimonials[current].text}"</p>
                <p className="mt-4 font-sans text-base font-bold text-secondary-foreground">— {testimonials[current].name}</p>
                <p className="font-sans text-sm text-accent-strong">{testimonials[current].role}</p>
              </motion.div>
            </AnimatePresence>
            <button
              type="button"
              onClick={next}
              className="absolute right-0 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-card/10 transition-colors hover:bg-card/20 md:-right-12"
              aria-label="Next testimonial"
            >
              <ChevronRight className="h-5 w-5 text-secondary-foreground" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-6 flex justify-center gap-2">
            {testimonials.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setCurrent(i)}
                className={`inline-flex h-11 w-11 items-center justify-center rounded-full transition-colors ${i === current ? "text-accent" : "text-card/50 hover:text-card"}`}
                aria-label={`Go to testimonial ${i + 1}`}
                aria-current={i === current ? "true" : undefined}
              >
                <span className={`block h-2.5 w-2.5 rounded-full ${i === current ? "bg-accent" : "bg-card/40"}`} aria-hidden="true" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
